'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import SentimentBadge, { SentimentType } from '@/components/ui/SentimentBadge';
import CallColumn from './CallColumn';
import Customer360Column from './Customer360Column';
import AIAssistColumn from './AIAssistColumn';
import TransferPanel from './TransferPanel';
import AIWrapUp from './AIWrapUp';
import PersistentCallBar from './PersistentCallBar';
import NonVoiceAIWrapUp, { LinkedTicket } from './NonVoiceAIWrapUp';
import SimulateDemoDropdown, { SimulateChannel } from '@/app/components/SimulateDemoDropdown';
import IncomingInteractionNotification, { IncomingChannel } from '@/app/components/IncomingInteractionNotification';

export type CallState = 'active' | 'ended' | 'ai-processing' | 'ai-draft-ready' | 'post-call';

type NonVoiceLifecycle =
  | 'active' |'wrap-up-confirm'   // End Conversation confirmation dialog
  | 'ai-processing'     // AI is processing
  | 'ai-draft-ready'    // AI draft ready, show wrap-up
  | 'completed';        // Interaction done

type InteractionChannel = 'PHONE' | 'WHATSAPP' | 'LIVE CHAT' | 'EMAIL' | 'X' | 'FACEBOOK' | 'INSTAGRAM';

type InteractionTab = {
  id: string;
  channel: InteractionChannel;
  citizen: string;
  citizenFull?: string;
  topic: string;
  active?: boolean;
  sentiment: SentimentType;
  unread?: number;
  status?: string;
  lifecycle?: NonVoiceLifecycle;
  linkedTicket?: LinkedTicket | null;
  mirRef?: string;
  notes?: string;
};

// Deterministic conversation sequences — no duplicates
const waConversationSequence = [
  { id: 1, from: 'citizen', text: 'Assalamualaikum. Saya nak tanya pasal status permohonan passport saya.', time: '14:28', status: 'read' },
  { id: 2, from: 'agent', text: 'Waalaikumsalam. Selamat datang ke MyGCC. Saya Nur Aisyah, boleh saya bantu puan?', time: '14:28', status: 'read' },
  { id: 3, from: 'citizen', text: 'Saya dah submit permohonan minggu lepas tapi masih belum dapat apa-apa update. Kenapa lama sangat?', time: '14:29', status: 'read' },
  { id: 4, from: 'citizen', text: 'Dah pergi kaunter pun kena balik semula. Sangat menyusahkan.', time: '14:29', status: 'read' },
];

const chatConversationSequence = [
  { id: 1, from: 'citizen', text: 'Salam, saya nak tahu dokumen apa yang diperlukan untuk renew passport.', time: '14:35', status: 'read' },
  { id: 2, from: 'agent', text: 'Salam Puan Siti. Untuk pembaharuan pasport, puan perlu menyediakan: IC asal, pasport lama, dan borang permohonan IMM.12.', time: '14:36', status: 'read' },
  { id: 3, from: 'citizen', text: 'Berapa lama proses renewal passport?', time: '14:37', status: 'read' },
];

const xConversationSequence = [
  { id: 1, from: 'citizen', text: 'Hi MyGCC, where can I check my government assistance application status?', time: '14:40', status: 'read', public: true },
];

const fbConversationSequence = [
  { id: 1, from: 'citizen', text: 'Salam, saya nak tahu status permohonan bantuan saya.', time: '14:42', status: 'read' },
];

const igConversationSequence = [
  { id: 1, from: 'citizen', text: 'Hi MyGCC, boleh bantu saya check status permohonan?', time: '14:44', status: 'read' },
];

// Citizen reply sequences — deterministic, no repeats
const waCitizenReplies = [
  'Terima kasih. Boleh saya tahu berapa lama lagi proses ini akan ambil masa?',
  'Baik, saya faham. Boleh saya terus ke kaunter esok pagi?',
  'Terima kasih banyak-banyak. Saya akan ikut langkah yang puan terangkan.',
];

const chatCitizenReplies = [
  'Kalau passport dah expired lebih setahun masih boleh renew ke?',
  'Baik, terima kasih. Boleh saya buat temujanji online?',
  'Terima kasih Puan Nur Aisyah. Saya faham sekarang.',
];

const xCitizenReplies = [
  'Thank you! That was very helpful.',
];

const fbCitizenReplies = [
  'Terima kasih banyak-banyak!',
];

const igCitizenReplies = [
  'Thank you so much! 🙏',
];

const initialTabs: InteractionTab[] = [
  { id: 'tab-phone-001', channel: 'PHONE', citizen: 'Ahmed', citizenFull: 'Ahmed Firdaus', topic: 'MySejahtera Account Update', active: true, sentiment: 'Concerned', lifecycle: 'active' },
  { id: 'tab-wa-002', channel: 'WHATSAPP', citizen: 'Nurul', citizenFull: 'Nurul Huda', topic: 'Passport Application Status', sentiment: 'Frustrated', unread: 2, lifecycle: 'active' },
  { id: 'tab-chat-003', channel: 'LIVE CHAT', citizen: 'Siti', citizenFull: 'Siti Mariam', topic: 'Passport Renewal Documentation', sentiment: 'Neutral', lifecycle: 'active' },
  { id: 'tab-email-004', channel: 'EMAIL', citizen: 'Daniel', citizenFull: 'Daniel Tan', topic: 'Status Permohonan STR', sentiment: 'Concerned', unread: 1, lifecycle: 'active' },
];

function getChannelIcon(channel: InteractionChannel): string {
  switch (channel) {
    case 'PHONE': return 'PhoneIcon';
    case 'WHATSAPP': return 'ChatBubbleOvalLeftEllipsisIcon';
    case 'LIVE CHAT': return 'ChatBubbleLeftRightIcon';
    case 'EMAIL': return 'EnvelopeIcon';
    case 'X': return 'HashtagIcon';
    case 'FACEBOOK': return 'UserGroupIcon';
    case 'INSTAGRAM': return 'CameraIcon';
    default: return 'ChatBubbleLeftRightIcon';
  }
}

function getChannelColor(channel: InteractionChannel): string {
  switch (channel) {
    case 'PHONE': return 'text-blue-600';
    case 'WHATSAPP': return 'text-green-600';
    case 'LIVE CHAT': return 'text-cyan-600';
    case 'EMAIL': return 'text-purple-600';
    case 'X': return 'text-slate-700';
    case 'FACEBOOK': return 'text-blue-700';
    case 'INSTAGRAM': return 'text-pink-600';
    default: return 'text-muted-foreground';
  }
}

function getSentimentEmoji(sentiment: SentimentType): string {
  switch (sentiment) {
    case 'Positive': return '😊';
    case 'Satisfied': return '🙂';
    case 'Neutral': return '😐';
    case 'Concerned': return '😕';
    case 'Frustrated': return '😣';
    case 'Angry': return '😠';
    default: return '😐';
  }
}

function getEndLabel(channel: InteractionChannel): string {
  switch (channel) {
    case 'LIVE CHAT': return 'End Conversation & Wrap-Up';
    case 'EMAIL': return 'Complete Email & Wrap-Up';
    default: return 'End Interaction & Wrap-Up';
  }
}

function getMirRef(channel: InteractionChannel): string {
  const ts = '20260816';
  const rand = Math.floor(10000 + Math.random() * 90000);
  return `MIR-SCR-${ts}-${rand}`;
}

const channelSimData: Record<SimulateChannel, { channel: InteractionChannel; citizen: string; citizenFull: string; topic: string; preview: string; sub?: string }> = {
  voice: { channel: 'PHONE', citizen: 'Ahmed', citizenFull: 'Ahmed Firdaus', topic: 'MySejahtera Account Update', preview: '', sub: '+60 12 *** 4581' },
  livechat: { channel: 'LIVE CHAT', citizen: 'Siti', citizenFull: 'Siti Mariam', topic: 'Passport Renewal Documentation', preview: 'Salam, saya nak tahu dokumen apa yang diperlukan untuk renew passport.' },
  whatsapp: { channel: 'WHATSAPP', citizen: 'Nurul', citizenFull: 'Nurul Huda', topic: 'Passport Application Status', preview: 'Saya dah submit permohonan minggu lepas tapi masih belum dapat apa-apa update.' },
  email: { channel: 'EMAIL', citizen: 'Daniel', citizenFull: 'Daniel Tan', topic: 'Status Permohonan STR', preview: 'Dear MyGCC, saya ingin mendapatkan status permohonan STR saya.' },
  x: { channel: 'X', citizen: '@amirhakim', citizenFull: '@amirhakim', topic: 'Application Status Enquiry', preview: 'Hi MyGCC, where can I check my government assistance application status?' },
  facebook: { channel: 'FACEBOOK', citizen: 'Farah', citizenFull: 'Farah Nadia', topic: 'Facebook Messenger', preview: 'Salam, saya nak tahu status permohonan bantuan saya.' },
  instagram: { channel: 'INSTAGRAM', citizen: '@farahnadia', citizenFull: '@farahnadia', topic: 'Instagram Direct Message', preview: 'Hi MyGCC, boleh bantu saya check status permohonan?' },
};

// ─── MIR Screen Recording Card ───────────────────────────────────────────────
function MIRScreenRecordingCard({ mirRef, lifecycle, channel, duration }: {
  mirRef: string;
  lifecycle: NonVoiceLifecycle;
  channel: InteractionChannel;
  duration: string;
}) {
  const isActive = lifecycle === 'active';
  const isCompleting = lifecycle === 'wrap-up-confirm' || lifecycle === 'ai-processing';
  const isCompleted = lifecycle === 'ai-draft-ready' || lifecycle === 'completed';

  return (
    <div className="border border-border rounded-xl p-3 bg-white">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Icon name="ComputerDesktopIcon" size={12} className="text-slate-600" />
          <span className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground">Mitel MIR Screen Recording</span>
        </div>
        {isActive && (
          <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-600 bg-red-50 text-red-600">
            <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
            Recording Active
          </span>
        )}
        {isCompleting && (
          <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-600 bg-amber-50 text-amber-600">
            <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
            Completing
          </span>
        )}
        {isCompleted && (
          <span className="text-[10px] px-1.5 py-0.5 rounded font-600 bg-green-50 text-green-600">
            Completed
          </span>
        )}
      </div>
      <div className="text-[11px] font-mono text-foreground font-600 mb-1.5">{mirRef}</div>
      <div className="grid grid-cols-2 gap-1 text-[10px] mb-1.5">
        <div className="flex justify-between"><span className="text-muted-foreground">Agent:</span><span className="font-600">Nur Aisyah</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Duration:</span><span className="font-600">{duration}</span></div>
        <div className="flex justify-between col-span-2"><span className="text-muted-foreground">Interaction:</span><span className="font-600">{channel === 'LIVE CHAT' ? 'Live Chat' : channel === 'WHATSAPP' ? 'WhatsApp' : channel === 'EMAIL' ? 'Email' : channel}</span></div>
      </div>
      <div className="text-[10px] text-muted-foreground">Agent interaction screen recording managed by Mitel MIR</div>
    </div>
  );
}

// ─── Create Ticket Form ───────────────────────────────────────────────────────
function CreateTicketForm({
  citizen, citizenFull, channel, topic, onCancel, onCreate,
}: {
  citizen: string;
  citizenFull: string;
  channel: InteractionChannel;
  topic: string;
  onCancel: () => void;
  onCreate: (ticket: LinkedTicket) => void;
}) {
  const channelLabel = channel === 'LIVE CHAT' ? 'Live Chat' : channel === 'WHATSAPP' ? 'WhatsApp' : channel === 'EMAIL' ? 'Email' : channel;
  const [subject, setSubject] = useState(topic);
  const [agency, setAgency] = useState(channel === 'EMAIL' ? 'KPKT' : 'Jabatan Imigresen Malaysia');
  const [category, setCategory] = useState(channel === 'EMAIL' ? 'Government Assistance' : 'Passport');
  const [subcategory, setSubcategory] = useState(channel === 'EMAIL' ? 'STR Application Status' : 'Application Status');
  const [priority, setPriority] = useState('Medium');
  const [description, setDescription] = useState(
    channel === 'EMAIL' ?'Citizen requesting status update on STR application submitted previously. Reference: STR-2026-084521.' :'Citizen contacted MyGCC regarding passport application status and renewal requirements.'
  );

  const handleCreate = () => {
    const ticketId = `MYGCC-TKT-2026-${Math.floor(10000 + Math.random() * 90000)}`;
    onCreate({ id: ticketId, subject, status: 'Open' });
  };

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center bg-black/40">
      <div className="bg-card border border-border rounded-2xl shadow-modal p-5 w-[480px] max-h-[90vh] overflow-y-auto slide-in-up">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="TicketIcon" size={16} className="text-primary" />
          </div>
          <div>
            <h2 className="text-[14px] font-700 text-foreground">Create Ticket</h2>
            <p className="text-[11px] text-muted-foreground">{citizenFull} · {channelLabel}</p>
          </div>
          <button onClick={onCancel} className="ml-auto p-1.5 rounded-lg hover:bg-muted transition-colors">
            <Icon name="XMarkIcon" size={15} className="text-muted-foreground" />
          </button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-700 text-muted-foreground mb-1">Citizen</label>
              <div className="input-field bg-muted/40 text-[12px] font-600">{citizenFull}</div>
            </div>
            <div>
              <label className="block text-[10px] font-700 text-muted-foreground mb-1">Channel</label>
              <div className="input-field bg-muted/40 text-[12px] font-600">{channelLabel}</div>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-700 text-muted-foreground mb-1">Subject</label>
            <input value={subject} onChange={e => setSubject(e.target.value)} className="input-field w-full text-[12px]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-700 text-muted-foreground mb-1">Agency</label>
              <input value={agency} onChange={e => setAgency(e.target.value)} className="input-field w-full text-[12px]" />
            </div>
            <div>
              <label className="block text-[10px] font-700 text-muted-foreground mb-1">Category</label>
              <input value={category} onChange={e => setCategory(e.target.value)} className="input-field w-full text-[12px]" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-700 text-muted-foreground mb-1">Subcategory</label>
              <input value={subcategory} onChange={e => setSubcategory(e.target.value)} className="input-field w-full text-[12px]" />
            </div>
            <div>
              <label className="block text-[10px] font-700 text-muted-foreground mb-1">Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value)} className="input-field w-full text-[12px]">
                <option>Low</option>
                <option>Medium</option>
                <option>High</option>
                <option>Critical</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-700 text-muted-foreground mb-1">
              Description <span className="text-primary font-500">✨ AI Pre-filled</span>
            </label>
            <textarea rows={3} value={description} onChange={e => setDescription(e.target.value)} className="input-field w-full resize-none text-[12px]" />
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button onClick={handleCreate} className="flex-1 btn-primary py-2.5 text-[13px] flex items-center justify-center gap-2">
            <Icon name="TicketIcon" size={14} />
            Create Ticket
          </button>
          <button onClick={onCancel} className="flex-1 btn-secondary py-2.5 text-[13px]">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Duplicate Ticket Warning ─────────────────────────────────────────────────
function DuplicateTicketWarning({
  ticket, onViewExisting, onCreateAnother, onCancel,
}: {
  ticket: LinkedTicket;
  onViewExisting: () => void;
  onCreateAnother: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center bg-black/40">
      <div className="bg-card border border-border rounded-2xl shadow-modal p-5 w-[400px] slide-in-up">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
            <Icon name="ExclamationTriangleIcon" size={16} className="text-amber-600" />
          </div>
          <div>
            <h2 className="text-[14px] font-700 text-foreground">Ticket Already Linked</h2>
            <p className="text-[11px] text-muted-foreground">A Ticket is already linked to this interaction.</p>
          </div>
        </div>
        <div className="bg-muted/40 rounded-xl p-3 mb-4">
          <div className="text-[12px] font-700 font-mono text-foreground">{ticket.id}</div>
          <div className="text-[11px] text-muted-foreground">{ticket.subject} · {ticket.status}</div>
        </div>
        <div className="flex flex-col gap-2">
          <button onClick={onViewExisting} className="btn-primary py-2.5 text-[13px] flex items-center justify-center gap-2">
            <Icon name="EyeIcon" size={14} />
            View Existing Ticket
          </button>
          <button onClick={onCreateAnother} className="btn-secondary py-2 text-[12px]">Create Another Ticket Anyway</button>
          <button onClick={onCancel} className="btn-ghost py-2 text-[12px] border border-border">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── End Conversation Confirmation ────────────────────────────────────────────
function EndConversationConfirm({
  citizen, channel, duration, linkedTicket, onContinue, onEndAndWrapUp,
}: {
  citizen: string;
  channel: InteractionChannel;
  duration: string;
  linkedTicket: LinkedTicket | null;
  onContinue: () => void;
  onEndAndWrapUp: () => void;
}) {
  const channelLabel = channel === 'LIVE CHAT' ? 'Live Chat' : channel === 'WHATSAPP' ? 'WhatsApp' : channel === 'EMAIL' ? 'Email' : channel;
  const endLabel = channel === 'EMAIL' ? 'Complete Email' : channel === 'LIVE CHAT' ? 'Live Chat' : 'Interaction';

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center bg-black/40">
      <div className="bg-card border border-border rounded-2xl shadow-modal p-5 w-[420px] slide-in-up">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
            <Icon name="StopCircleIcon" size={16} className="text-slate-600" />
          </div>
          <div>
            <h2 className="text-[14px] font-700 text-foreground">End {endLabel}?</h2>
            <p className="text-[11px] text-muted-foreground">{channelLabel} · {citizen}</p>
          </div>
        </div>
        <div className="space-y-2 mb-4">
          {[
            { label: 'Citizen', value: citizen },
            { label: 'Duration', value: duration },
            ...(linkedTicket ? [{ label: 'Linked Ticket', value: linkedTicket.id }] : []),
          ].map(row => (
            <div key={row.label} className="flex items-center justify-between text-[12px] bg-muted/40 rounded-xl px-3 py-2">
              <span className="text-muted-foreground">{row.label}</span>
              <span className="font-600 text-foreground font-mono text-[11px]">{row.value}</span>
            </div>
          ))}
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-[11px] text-blue-700">
          The conversation will end and CX AI Assist will prepare the interaction Wrap-Up for your review.
        </div>
        <div className="flex gap-3">
          <button onClick={onContinue} className="flex-1 btn-secondary py-2.5 text-[13px]">Continue Conversation</button>
          <button onClick={onEndAndWrapUp} className="flex-1 btn-primary py-2.5 text-[13px] flex items-center justify-center gap-2">
            <Icon name="SparklesIcon" size={14} />
            End & Generate Wrap-Up
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Linked Ticket Card ───────────────────────────────────────────────────────
function LinkedTicketCard({ ticket, onView, onUnlink }: {
  ticket: LinkedTicket;
  onView: () => void;
  onUnlink: () => void;
}) {
  return (
    <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2">
      <Icon name="TicketIcon" size={13} className="text-blue-600 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-700 text-blue-700 uppercase tracking-wide">Linked Ticket</div>
        <button onClick={onView} className="text-[12px] font-700 text-blue-800 font-mono hover:underline truncate block">{ticket.id}</button>
        <div className="text-[10px] text-blue-600 truncate">{ticket.subject} · {ticket.status}</div>
      </div>
      <div className="flex flex-col gap-1 flex-shrink-0">
        <button onClick={onView} className="text-[10px] text-blue-700 font-600 hover:underline">View Ticket</button>
        <button onClick={onUnlink} className="text-[10px] text-red-500 font-600 hover:underline">Unlink</button>
      </div>
    </div>
  );
}

// ─── Ticket Detail View ───────────────────────────────────────────────────────
function TicketDetailView({ ticket, citizen, channel, mirRef, onBack }: {
  ticket: LinkedTicket;
  citizen: string;
  channel: InteractionChannel;
  mirRef: string;
  onBack: () => void;
}) {
  const channelLabel = channel === 'LIVE CHAT' ? 'Live Chat' : channel === 'WHATSAPP' ? 'WhatsApp' : channel === 'EMAIL' ? 'Email' : channel;
  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin p-5 bg-background">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={onBack} className="flex items-center gap-1.5 text-[12px] text-primary font-600 hover:underline">
          <Icon name="ArrowLeftIcon" size={13} />
          Back to Interaction
        </button>
      </div>
      <div className="card-elevated p-5 mb-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[11px] font-700 text-muted-foreground uppercase tracking-wide mb-1">Ticket</div>
            <div className="text-[18px] font-700 text-foreground font-mono">{ticket.id}</div>
            <div className="text-[14px] text-foreground mt-0.5">{ticket.subject}</div>
          </div>
          <span className="text-[11px] px-2.5 py-1 rounded-lg font-700 bg-green-50 text-green-700 border border-green-200">{ticket.status}</span>
        </div>
        <div className="grid grid-cols-2 gap-3 text-[12px]">
          {[
            { label: 'Customer', value: citizen },
            { label: 'Channel', value: channelLabel },
            { label: 'Assigned To', value: 'Nur Aisyah' },
            { label: 'Priority', value: 'Medium' },
            { label: 'Created', value: '16 Aug 2026' },
            { label: 'SLA', value: '4h remaining' },
          ].map(row => (
            <div key={row.label} className="bg-muted/40 rounded-xl px-3 py-2">
              <div className="text-muted-foreground text-[10px]">{row.label}</div>
              <div className="font-600 text-foreground">{row.value}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="card-elevated p-4 mb-4">
        <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-2">AI Summary</div>
        <div className="flex items-center gap-1.5 mb-2">
          <span className="text-[9px] text-primary font-600 bg-primary/10 px-1.5 py-0.5 rounded">✨ AI Generated · Agent Reviewed</span>
        </div>
        <p className="text-[12px] text-foreground leading-relaxed">
          Citizen contacted MyGCC via {channelLabel} regarding their enquiry. Agent provided relevant information and guidance. Issue resolved at first contact.
        </p>
      </div>
      <div className="card-elevated p-4 mb-4">
        <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-2">MIR Screen Recording Reference</div>
        <div className="flex items-center gap-2 text-[12px]">
          <Icon name="ComputerDesktopIcon" size={13} className="text-slate-500" />
          <span className="font-700 font-mono">{mirRef}</span>
          <span className="ml-auto text-[10px] text-green-600 font-600 bg-green-50 px-1.5 py-0.5 rounded">Archived</span>
        </div>
        <div className="text-[10px] text-muted-foreground mt-1">Agent interaction screen recording managed by Mitel MIR</div>
      </div>
      <div className="card-elevated p-4">
        <div className="text-[11px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Interaction History</div>
        <div className="space-y-2 text-[12px]">
          <div className="flex items-center gap-2 p-2 bg-muted/40 rounded-xl">
            <Icon name={getChannelIcon(channel) as Parameters<typeof Icon>[0]['name']} size={13} className={getChannelColor(channel)} />
            <span className="font-600">{channelLabel}</span>
            <span className="text-muted-foreground">· 16 Aug 2026 · Nur Aisyah</span>
            <span className="ml-auto text-green-600 font-600">Resolved</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Notes Panel ──────────────────────────────────────────────────────────────
function NotesPanel({ notes, onChange, onClose }: {
  notes: string;
  onChange: (v: string) => void;
  onClose: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => { setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2000); }, 800);
  };

  return (
    <div className="border-t border-border bg-card px-4 py-3 flex-shrink-0">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Icon name="PencilSquareIcon" size={13} className="text-primary" />
          <span className="text-[12px] font-700 text-foreground">Agent Notes</span>
        </div>
        <div className="flex items-center gap-2">
          {saving && <span className="text-[10px] text-muted-foreground">Saving...</span>}
          {saved && <span className="text-[10px] text-green-600 font-600">Saved ✓</span>}
          <button onClick={handleSave} className="btn-secondary px-2.5 py-1 text-[11px]">Save</button>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted transition-colors">
            <Icon name="XMarkIcon" size={13} className="text-muted-foreground" />
          </button>
        </div>
      </div>
      <textarea
        rows={3}
        value={notes}
        onChange={e => onChange(e.target.value)}
        placeholder="Add notes about this interaction..."
        className="input-field w-full resize-none text-[12px]"
      />
    </div>
  );
}

// ─── AI Processing State ──────────────────────────────────────────────────────
function AIProcessingState({ citizen }: { citizen: string }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-8 space-y-4 bg-background">
      <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center">
        <Icon name="SparklesIcon" size={24} className="text-blue-600" />
      </div>
      <div className="flex gap-1.5">
        {[0, 1, 2].map(i => (
          <span key={i} className="w-2.5 h-2.5 bg-blue-500 rounded-full ai-listening-dot" style={{ animationDelay: `${i * 0.16}s` }} />
        ))}
      </div>
      <div className="text-[15px] font-700 text-blue-700">AI Processing</div>
      <div className="text-[12px] text-muted-foreground text-center max-w-[280px]">
        CX AI Assist is analysing the conversation with {citizen} and preparing the interaction summary.
      </div>
      <div className="text-[11px] text-muted-foreground">You can handle other interactions while AI processes.</div>
    </div>
  );
}

// ─── Knowledge Modal ──────────────────────────────────────────────────────────
function KnowledgeModal({ onClose, onUse }: { onClose: () => void; onUse: (text: string) => void }) {
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40">
      <div className="bg-card border border-border rounded-2xl shadow-modal p-6 w-[520px] max-h-[80vh] overflow-y-auto slide-in-up">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded font-700">✓ Verified Government Knowledge</span>
            </div>
            <h2 className="text-[16px] font-700 text-foreground">Malaysia Passport Renewal Requirements</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="XMarkIcon" size={16} className="text-muted-foreground" />
          </button>
        </div>
        <div className="space-y-3 text-[13px] text-foreground">
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground border-b border-border pb-3">
            <span>Agency: <strong>Jabatan Imigresen Malaysia</strong></span>
            <span>·</span>
            <span>Last Updated: 10 Aug 2026</span>
          </div>
          <p className="leading-relaxed">Untuk pembaharuan pasport Malaysia, pemohon perlu menyediakan dokumen-dokumen berikut:</p>
          <ol className="list-decimal list-inside space-y-2 pl-2">
            <li>Kad Pengenalan (IC) asal yang masih sah</li>
            <li>Pasport lama (jika ada)</li>
            <li>Borang permohonan IMM.12 yang telah dilengkapkan</li>
            <li>Bayaran pemprosesan (RM200 untuk 5 tahun, RM300 untuk 10 tahun)</li>
            <li>Gambar pasport terkini (2 keping, saiz 35mm x 50mm)</li>
          </ol>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <div className="text-[11px] font-700 text-blue-700 mb-1">Nota Penting</div>
            <div className="text-[11px] text-blue-700">Pasport yang telah tamat tempoh lebih daripada 5 tahun memerlukan proses semakan tambahan. Masa pemprosesan biasa adalah 3-5 hari bekerja.</div>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={() => { onUse("Untuk pembaharuan pasport, puan perlu menyediakan IC asal, pasport lama, borang IMM.12, bayaran pemprosesan dan gambar pasport terkini. Masa pemprosesan adalah 3-5 hari bekerja."); onClose(); }}
            className="flex-1 btn-primary py-2.5 text-[13px] flex items-center justify-center gap-2">
            <Icon name="PaperAirplaneIcon" size={13} />
            Use in Response
          </button>
          <button onClick={onClose} className="flex-1 btn-secondary py-2.5 text-[13px]">Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Consult Panel ────────────────────────────────────────────────────────────
function ConsultPanel({ onClose, onComplete }: { onClose: () => void; onComplete: () => void }) {
  const [selected, setSelected] = useState<string | null>(null);
  const [consulting, setConsulting] = useState(false);
  const agencies = ['JIM', 'JPJ', 'JPN', 'KESUMA', 'MOH'];

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[14px] font-700 text-foreground">Consult With</h3>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
          <Icon name="XMarkIcon" size={14} className="text-muted-foreground" />
        </button>
      </div>
      <input type="text" placeholder="Search agent / agency..." className="input-field w-full" />
      <div className="space-y-1">
        {agencies.map(a => (
          <button key={a} onClick={() => setSelected(a)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-colors text-left ${selected === a ? 'border-primary bg-secondary' : 'border-border hover:bg-muted'}`}>
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-[11px] font-700 text-primary flex-shrink-0">{a[0]}</div>
            <div>
              <div className="text-[13px] font-600 text-foreground">{a}</div>
              <div className="text-[10px] text-muted-foreground">CX Controller · MyGovNet</div>
            </div>
            {selected === a && <Icon name="CheckCircleIcon" size={14} className="text-primary ml-auto" />}
          </button>
        ))}
      </div>
      {consulting && selected ? (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
          <div className="text-[13px] font-600 text-blue-700 mb-1">Consulting with {selected}</div>
          <div className="text-[11px] text-blue-600">Ahmed Firdaus is on hold</div>
          <div className="flex gap-2 mt-3">
            <button onClick={onClose} className="flex-1 btn-secondary py-2 text-[12px]">Return to Caller</button>
            <button onClick={() => { onComplete(); onClose(); }} className="flex-1 btn-primary py-2 text-[12px]">Complete Transfer</button>
          </div>
        </div>
      ) : (
        <button onClick={() => { if (!selected) return; setConsulting(true); toast.info(`Consulting with ${selected}`, { description: 'Ahmed Firdaus placed on hold' }); }}
          disabled={!selected} className="w-full btn-primary py-2.5 text-[13px] disabled:opacity-50">
          Start Consult
        </button>
      )}
    </div>
  );
}

// ─── Social Media Workspace ───────────────────────────────────────────────────
function SocialWorkspace({
  tab, messages, draft, setDraft, onSend, onEndInteraction, aiSuggestion, onUseAI, lifecycle,
}: {
  tab: InteractionTab;
  messages: { id: number; from: string; text: string; time: string; status: string; public?: boolean }[];
  draft: string;
  setDraft: (v: string) => void;
  onSend: () => void;
  onEndInteraction: () => void;
  aiSuggestion: string;
  onUseAI: () => void;
  lifecycle: NonVoiceLifecycle;
}) {
  const isX = tab.channel === 'X';
  const isFB = tab.channel === 'FACEBOOK';
  const channelLabel = isX ? 'X (Twitter)' : isFB ? 'Facebook Messenger' : 'Instagram Direct';
  const channelColor = isX ? 'text-slate-700' : isFB ? 'text-blue-700' : 'text-pink-600';
  const isEnded = lifecycle !== 'active';

  return (
    <div className="flex-1 flex min-h-0">
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-card flex-shrink-0">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-700 text-[13px] ${isX ? 'bg-slate-100 text-slate-700' : isFB ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-600'}`}>
            {(tab.citizenFull || tab.citizen)[0]}
          </div>
          <div>
            <div className="text-[13px] font-700 text-foreground">{tab.citizenFull || tab.citizen}</div>
            <div className={`flex items-center gap-1.5 text-[11px] ${channelColor}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-current" />
              {channelLabel}
              {isX && messages[0]?.public && <span className="ml-1 text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-600">Public Reply</span>}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <SentimentBadge sentiment={tab.sentiment} showBg />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-3 bg-background">
          {isX && (
            <div className="flex items-center gap-2 border border-slate-200 bg-slate-50 rounded-xl px-3 py-2 text-[11px] text-slate-600">
              <Icon name="InformationCircleIcon" size={12} />
              <span>Public interaction — replies will be visible publicly. Use Direct Message for sensitive information.</span>
            </div>
          )}
          {messages.map(msg => (
            <div key={msg.id} className={`flex ${msg.from === 'agent' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[70%] px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed ${
                msg.from === 'agent' ? 'bg-primary text-white rounded-br-sm' : 'bg-white border border-border text-foreground rounded-bl-sm shadow-sm'
              }`}>
                {msg.text}
                <div className={`text-[10px] mt-1 ${msg.from === 'agent' ? 'text-white/60' : 'text-muted-foreground'} text-right`}>{msg.time}</div>
              </div>
            </div>
          ))}
        </div>
        {!isEnded && (
          <div className="px-4 py-2 border-t border-border bg-ai-bg">
            <div className="flex items-start gap-2">
              <Icon name="SparklesIcon" size={13} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-700 text-primary mb-1">AI Suggested Reply <span className="text-muted-foreground font-500">· Agent approval required · Not sent automatically</span></div>
                <div className="text-[12px] text-foreground italic bg-white border border-ai-border rounded-xl px-3 py-2 mb-1.5">{aiSuggestion}</div>
                <div className="flex gap-1.5">
                  <button onClick={onUseAI} className="btn-secondary px-2.5 py-1 text-[11px]">Use</button>
                  <button onClick={() => { onUseAI(); }} className="btn-ghost px-2.5 py-1 text-[11px] border border-border">Edit</button>
                </div>
              </div>
            </div>
          </div>
        )}
        {!isEnded && (
          <div className="flex items-center gap-2 px-4 py-3 border-t border-border bg-card flex-shrink-0">
            <button onClick={() => toast.info('Attach file')} className="p-2 rounded-xl hover:bg-muted transition-colors flex-shrink-0">
              <Icon name="PaperClipIcon" size={15} className="text-muted-foreground" />
            </button>
            <input type="text" placeholder={isX ? `Reply to ${tab.citizen}...` : 'Type a message...'}
              value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === 'Enter' && onSend()}
              className="flex-1 input-field" />
            {isX && <span className="text-[11px] text-muted-foreground flex-shrink-0">{280 - draft.length}</span>}
            <button onClick={onSend} disabled={!draft.trim()} className="btn-primary px-3 py-2 flex items-center gap-1.5 flex-shrink-0 disabled:opacity-50">
              <Icon name="PaperAirplaneIcon" size={14} />
              Send
            </button>
          </div>
        )}
        <div className="flex items-center gap-2 px-4 py-2 border-t border-border bg-muted/30 flex-shrink-0">
          <button onClick={() => toast.info('Creating ticket')} className="btn-secondary px-2.5 py-1.5 text-[11px] flex items-center gap-1.5">
            <Icon name="PlusIcon" size={12} />
            Create Ticket
          </button>
          <button onClick={() => toast.info('Linking case')} className="btn-ghost px-2.5 py-1.5 text-[11px] border border-border flex items-center gap-1.5">
            <Icon name="LinkIcon" size={12} />
            Link Case
          </button>
          {!isEnded && (
            <button onClick={onEndInteraction} className="ml-auto btn-primary px-3 py-1.5 text-[11px] flex items-center gap-1.5 bg-slate-700 hover:bg-slate-800">
              <Icon name="StopCircleIcon" size={12} />
              End Interaction & Wrap-Up
            </button>
          )}
        </div>
      </div>
      <div className="w-72 flex-shrink-0 border-l border-border overflow-y-auto scrollbar-thin p-4" style={{ background: 'var(--ai-bg)' }}>
        <div className="flex items-center gap-2 mb-4">
          <Icon name="SparklesIcon" size={14} className="text-primary" />
          <h3 className="text-[13px] font-700 text-primary">CX AI Assist</h3>
          <span className="ai-badge">AI</span>
        </div>
        <div className="space-y-3">
          <div className="card-elevated p-3">
            <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Sentiment</div>
            <SentimentBadge sentiment={tab.sentiment} showBg confidence={87} size="md" />
          </div>
          <div className="card-elevated p-3">
            <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Detected Intent</div>
            <div className="text-[13px] font-700 text-foreground">{tab.topic}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">Agency: Relevant Government Agency</div>
          </div>
          <div className="card-elevated p-3">
            <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Suggested Knowledge</div>
            <div className="border border-primary/30 bg-secondary/50 rounded-xl p-2.5">
              <div className="text-[12px] font-600 text-foreground mb-1">Government Assistance Status Check</div>
              <div className="text-[10px] text-muted-foreground mb-1.5">MyGCC · Verified</div>
              <button onClick={() => toast.info('Opening knowledge article')} className="text-[11px] text-primary font-600 hover:underline">Open Article →</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Non-Voice Chat Workspace (WhatsApp / Live Chat) ─────────────────────────
function ChatWorkspace({
  tab, messages, draft, setDraft, onSend, onEndInteraction, aiSuggestion, onUseAI,
  lifecycle, linkedTicket, mirRef, interactionDuration,
  onCreateTicket, onViewTicket, onUnlinkTicket, onOpenKnowledge, showNotes, onToggleNotes,
}: {
  tab: InteractionTab;
  messages: { id: number; from: string; text: string; time: string; status: string }[];
  draft: string;
  setDraft: (v: string) => void;
  onSend: () => void;
  onEndInteraction: () => void;
  aiSuggestion: string;
  onUseAI: () => void;
  lifecycle: NonVoiceLifecycle;
  linkedTicket: LinkedTicket | null;
  mirRef: string;
  interactionDuration: string;
  onCreateTicket: () => void;
  onViewTicket: () => void;
  onUnlinkTicket: () => void;
  onOpenKnowledge: () => void;
  showNotes: boolean;
  onToggleNotes: () => void;
}) {
  const isWA = tab.channel === 'WHATSAPP';
  const isChat = tab.channel === 'LIVE CHAT';
  const isEnded = lifecycle !== 'active';
  const isProcessing = lifecycle === 'ai-processing';
  const endLabel = getEndLabel(tab.channel);

  const statusLabel = lifecycle === 'active' ? '🟢 Online' :
    lifecycle === 'wrap-up-confirm' ? 'Conversation Ending...' :
    lifecycle === 'ai-processing' ? 'AI Processing' :
    lifecycle === 'ai-draft-ready' ? 'AI Draft Ready' : 'Conversation Ended';

  return (
    <div className="flex-1 flex min-h-0">
      <div className="flex-1 flex flex-col min-h-0">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-card flex-shrink-0">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-700 text-[13px] ${isWA ? 'bg-green-100 text-green-700' : 'bg-cyan-100 text-cyan-700'}`}>
            {(tab.citizenFull || tab.citizen).split(' ').map(n => n[0]).join('').slice(0, 2)}
          </div>
          <div>
            <div className="text-[13px] font-700 text-foreground">{tab.citizenFull || tab.citizen}</div>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span className={`w-1.5 h-1.5 rounded-full ${isEnded ? 'bg-slate-400' : 'bg-green-500'}`} />
              {statusLabel} · {isWA ? 'WhatsApp' : 'Live Chat'} · {tab.topic}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <SentimentBadge sentiment={tab.sentiment} showBg />
            <button onClick={() => toast.info(`Customer 360 — ${tab.citizenFull || tab.citizen}`)} className="btn-secondary px-2.5 py-1 text-[11px] flex items-center gap-1">
              <Icon name="UserCircleIcon" size={12} />
              Customer 360
            </button>
          </div>
        </div>

        {/* MIR Screen Recording */}
        <div className="px-4 pt-3 flex-shrink-0">
          <MIRScreenRecordingCard mirRef={mirRef} lifecycle={lifecycle} channel={tab.channel} duration={interactionDuration} />
        </div>

        {/* Linked Ticket */}
        {linkedTicket && (
          <div className="px-4 pt-2 flex-shrink-0">
            <LinkedTicketCard ticket={linkedTicket} onView={onViewTicket} onUnlink={onUnlinkTicket} />
          </div>
        )}

        {/* Messages */}
        {isProcessing ? (
          <AIProcessingState citizen={tab.citizenFull || tab.citizen} />
        ) : (
          <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-3 bg-background">
            {isWA && (
              <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 text-[11px] text-blue-700">
                <Icon name="ClockIcon" size={12} className="flex-shrink-0" />
                <span><strong>Previous interaction found</strong> — WhatsApp 12 Aug 2026 · Passport Renewal Documentation</span>
                <button onClick={() => toast.info('Previous context: WhatsApp 12 Aug 2026 — Passport Renewal Documentation. Resolved. Ticket: TK-2026-10211')} className="ml-auto font-700 hover:underline flex-shrink-0">View Context →</button>
              </div>
            )}
            {messages.map(msg => (
              <div key={msg.id} className={`flex ${msg.from === 'agent' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[70%] px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed ${
                  msg.from === 'agent' ? 'bg-primary text-white rounded-br-sm' : 'bg-white border border-border text-foreground rounded-bl-sm shadow-sm'
                }`}>
                  {msg.text}
                  <div className={`text-[10px] mt-1 ${msg.from === 'agent' ? 'text-white/60' : 'text-muted-foreground'} text-right`}>
                    {msg.time}
                    {msg.from === 'agent' && <span className="ml-1">{msg.status === 'read' ? '✓✓' : '✓'}</span>}
                  </div>
                </div>
              </div>
            ))}
            {isEnded && !isProcessing && (
              <div className="flex justify-center">
                <span className="text-[11px] text-muted-foreground bg-muted/60 px-3 py-1.5 rounded-full">Conversation ended</span>
              </div>
            )}
          </div>
        )}

        {/* Notes Panel */}
        {showNotes && !isProcessing && (
          <NotesPanel notes={tab.notes || ''} onChange={() => {}} onClose={onToggleNotes} />
        )}

        {/* AI Suggestion */}
        {!isEnded && (
          <div className="px-4 py-2 border-t border-border bg-ai-bg flex-shrink-0">
            <div className="flex items-start gap-2">
              <Icon name="SparklesIcon" size={13} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-700 text-primary mb-1">AI Suggested Reply <span className="text-muted-foreground font-500">· Agent approval required · Not sent automatically</span></div>
                <div className="text-[12px] text-foreground italic bg-white border border-ai-border rounded-xl px-3 py-2 mb-1.5">{aiSuggestion}</div>
                <div className="flex gap-1.5">
                  <button onClick={onUseAI} className="btn-secondary px-2.5 py-1 text-[11px]">Use</button>
                  <button onClick={() => { onUseAI(); }} className="btn-ghost px-2.5 py-1 text-[11px] border border-border">Edit</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Composer */}
        {!isEnded && (
          <div className="flex items-center gap-2 px-4 py-3 border-t border-border bg-card flex-shrink-0">
            <button onClick={() => toast.info('Attach file')} className="p-2 rounded-xl hover:bg-muted transition-colors flex-shrink-0">
              <Icon name="PaperClipIcon" size={15} className="text-muted-foreground" />
            </button>
            {isWA && (
              <button onClick={() => toast.info('Insert template')} className="p-2 rounded-xl hover:bg-muted transition-colors flex-shrink-0">
                <Icon name="DocumentTextIcon" size={15} className="text-muted-foreground" />
              </button>
            )}
            <input type="text" placeholder="Type a message..." value={draft}
              onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === 'Enter' && onSend()}
              className="flex-1 input-field" />
            <button onClick={onSend} disabled={!draft.trim()} className="btn-primary px-3 py-2 flex items-center gap-1.5 flex-shrink-0 disabled:opacity-50">
              <Icon name="PaperAirplaneIcon" size={14} />
              Send
            </button>
          </div>
        )}

        {/* Actions bar */}
        <div className="flex items-center gap-2 px-4 py-2 border-t border-border bg-muted/30 flex-shrink-0 flex-wrap gap-y-1.5">
          {!linkedTicket ? (
            <button onClick={onCreateTicket} className="btn-secondary px-2.5 py-1.5 text-[11px] flex items-center gap-1.5">
              <Icon name="PlusIcon" size={12} />
              Create Ticket
            </button>
          ) : (
            <div className="text-[11px] text-blue-700 font-600 flex items-center gap-1">
              <Icon name="TicketIcon" size={12} />
              <button onClick={onViewTicket} className="hover:underline font-mono">{linkedTicket.id}</button>
              <span className="text-muted-foreground">· {linkedTicket.status}</span>
            </div>
          )}
          {isChat && (
            <button onClick={onOpenKnowledge} className="btn-ghost px-2.5 py-1.5 text-[11px] border border-border flex items-center gap-1.5">
              <Icon name="BookOpenIcon" size={12} />
              Knowledge
            </button>
          )}
          <button onClick={onToggleNotes} className="btn-ghost px-2.5 py-1.5 text-[11px] border border-border flex items-center gap-1.5">
            <Icon name="PencilIcon" size={12} />
            Notes
          </button>
          {!isEnded && (
            <button onClick={onEndInteraction}
              className="ml-auto btn-primary px-3 py-1.5 text-[11px] flex items-center gap-1.5 bg-slate-700 hover:bg-slate-800 border-0">
              <Icon name="StopCircleIcon" size={12} />
              {endLabel}
            </button>
          )}
        </div>
      </div>

      {/* AI Assist Panel */}
      <div className="w-72 flex-shrink-0 border-l border-border overflow-y-auto scrollbar-thin p-4" style={{ background: 'var(--ai-bg)' }}>
        <div className="flex items-center gap-2 mb-4">
          <Icon name="SparklesIcon" size={14} className="text-primary" />
          <h3 className="text-[13px] font-700 text-primary">CX AI Assist</h3>
          <span className="ai-badge">AI</span>
        </div>
        <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-[12px] font-600 mb-4 ${
          isEnded ? 'bg-green-50 border-green-200 text-green-700' : 'bg-blue-50 border-blue-200 text-blue-700'
        }`}>
          {isEnded ? (
            <><Icon name="CheckCircleIcon" size={13} />Interaction Analysis Complete</>
          ) : (
            <><div className="flex gap-0.5">{[0,1,2].map(i => <span key={i} className="w-1.5 h-1.5 bg-blue-500 rounded-full ai-listening-dot" style={{ animationDelay: `${i * 0.16}s` }} />)}</div>Analysing Conversation</>
          )}
        </div>
        <div className="space-y-3">
          <div className="card-elevated p-3">
            <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Live Sentiment</div>
            <SentimentBadge sentiment={tab.sentiment} showBg confidence={isWA ? 94 : 82} size="md" />
            {isWA && tab.sentiment === 'Frustrated' && (
              <div className="mt-2 text-[11px] text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-2 py-1.5">
                Citizen frustrated — repeated failed attempts detected
              </div>
            )}
          </div>
          <div className="card-elevated p-3">
            <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Detected Intent</div>
            <div className="text-[13px] font-700 text-foreground">{tab.topic}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">Agency: Jabatan Imigresen Malaysia</div>
            <div className="flex items-center gap-2 mt-1.5">
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width: isWA ? '94%' : '91%' }} />
              </div>
              <span className="text-[11px] font-700 text-primary">{isWA ? '94%' : '91%'}</span>
            </div>
          </div>
          <div className="card-elevated p-3">
            <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Suggested Knowledge</div>
            <div className="border border-primary/30 bg-secondary/50 rounded-xl p-2.5">
              <div className="text-[12px] font-600 text-foreground mb-1">Malaysia Passport Renewal Requirements</div>
              <div className="text-[10px] text-muted-foreground mb-1.5">Jabatan Imigresen · Verified · Updated 10 Aug 2026</div>
              <button onClick={onOpenKnowledge} className="text-[11px] text-primary font-600 hover:underline">Open Article →</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Email Workspace ──────────────────────────────────────────────────────────
function EmailWorkspace({
  tab, lifecycle, linkedTicket, mirRef, interactionDuration,
  onCreateTicket, onViewTicket, onUnlinkTicket, onEndInteraction, showNotes, onToggleNotes,
}: {
  tab: InteractionTab;
  lifecycle: NonVoiceLifecycle;
  linkedTicket: LinkedTicket | null;
  mirRef: string;
  interactionDuration: string;
  onCreateTicket: () => void;
  onViewTicket: () => void;
  onUnlinkTicket: () => void;
  onEndInteraction: () => void;
  showNotes: boolean;
  onToggleNotes: () => void;
}) {
  const [replyText, setReplyText] = useState('');
  const isEnded = lifecycle !== 'active';

  return (
    <div className="flex-1 flex min-h-0">
      <div className="flex-1 overflow-y-auto scrollbar-thin p-5 bg-background">
        {/* MIR Screen Recording */}
        <div className="mb-4">
          <MIRScreenRecordingCard mirRef={mirRef} lifecycle={lifecycle} channel={tab.channel} duration={interactionDuration} />
        </div>

        {/* Linked Ticket */}
        {linkedTicket && (
          <div className="mb-4">
            <LinkedTicketCard ticket={linkedTicket} onView={onViewTicket} onUnlink={onUnlinkTicket} />
          </div>
        )}

        <div className="card-elevated p-4 mb-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h2 className="text-[15px] font-700 text-foreground mb-1">Status Permohonan STR</h2>
              <div className="space-y-1 text-[12px]">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground w-12">From:</span>
                  <span className="font-600 text-foreground">Daniel Tan &lt;d.tan@email.my&gt;</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground w-12">To:</span>
                  <span className="text-foreground">mygcc@gov.my</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground w-12">Date:</span>
                  <span className="text-foreground">16 Aug 2026, 14:20</span>
                </div>
              </div>
            </div>
            <SentimentBadge sentiment="Concerned" showBg />
          </div>
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl mb-3">
            <Icon name="SparklesIcon" size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-[11px] font-700 text-amber-700 mb-0.5">CX AI Analysis</div>
              <div className="text-[11px] text-amber-700">
                <strong>Sentiment:</strong> 😕 Concerned (88%) · <strong>Intent:</strong> STR Application Status · <strong>Agency:</strong> KPKT
              </div>
              <button onClick={() => toast.info('Opening related case: MYGCC-2026-008375')} className="text-[11px] text-amber-700 font-700 hover:underline mt-1">
                Related case found — MYGCC-2026-008375 · Review before responding →
              </button>
            </div>
          </div>
          <div className="bg-muted/30 rounded-xl p-4 text-[13px] text-foreground leading-relaxed border border-border whitespace-pre-line">
            {`Dear MyGCC,\n\nSaya ingin mendapatkan status permohonan STR saya yang telah dihantar sebelum ini. Nombor rujukan permohonan saya adalah STR-2026-084521.\n\nSaya telah menunggu selama dua minggu tetapi belum menerima sebarang maklum balas. Mohon bantuan untuk semakan dan kemas kini status permohonan saya.\n\nTerima kasih.\n\nDaniel Tan\n+60 16 *** 7742`}
          </div>
        </div>

        {!isEnded && (
          <div className="card-elevated p-4">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-[13px] font-700">Reply</h3>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => toast.info('Forwarding email')} className="btn-ghost px-2.5 py-1 text-[11px] border border-border flex items-center gap-1.5">
                  <Icon name="ArrowUturnRightIcon" size={12} />
                  Forward
                </button>
                <button onClick={() => setReplyText("Terima kasih kerana menghubungi MyGCC. Kami telah menerima pertanyaan anda mengenai status permohonan STR anda. Kami akan menyemak status permohonan anda dan memberikan kemas kini dalam masa 1-2 hari bekerja.")}
                  className="btn-secondary px-2.5 py-1 text-[11px] flex items-center gap-1.5">
                  <Icon name="SparklesIcon" size={12} />
                  Use AI Draft
                </button>
              </div>
            </div>
            <textarea placeholder="Type your reply..." rows={5} value={replyText} onChange={e => setReplyText(e.target.value)} className="input-field resize-none mb-3 w-full" />
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => toast.success('Email sent successfully')} className="btn-primary px-4 py-2 text-[12px] flex items-center gap-1.5">
                <Icon name="PaperAirplaneIcon" size={13} />
                Send Reply
              </button>
              <button onClick={() => toast.info('Draft saved')} className="btn-secondary px-3 py-2 text-[12px] flex items-center gap-1.5">
                <Icon name="DocumentTextIcon" size={13} />
                Save Draft
              </button>
              {!linkedTicket ? (
                <button onClick={onCreateTicket} className="btn-secondary px-3 py-2 text-[12px] flex items-center gap-1.5">
                  <Icon name="PlusIcon" size={13} />
                  Create Ticket
                </button>
              ) : (
                <button onClick={onViewTicket} className="btn-secondary px-3 py-2 text-[12px] flex items-center gap-1.5">
                  <Icon name="TicketIcon" size={13} />
                  View Ticket
                </button>
              )}
              <button onClick={onToggleNotes} className="btn-ghost px-3 py-2 text-[12px] border border-border flex items-center gap-1.5">
                <Icon name="PencilIcon" size={13} />
                Notes
              </button>
              <button onClick={onEndInteraction} className="ml-auto btn-primary px-3 py-2 text-[12px] bg-slate-700 hover:bg-slate-800 border-0 flex items-center gap-1.5">
                <Icon name="StopCircleIcon" size={13} />
                Complete Email & Wrap-Up
              </button>
            </div>
          </div>
        )}

        {showNotes && (
          <div className="mt-4">
            <NotesPanel notes={tab.notes || ''} onChange={() => {}} onClose={onToggleNotes} />
          </div>
        )}
      </div>

      {/* AI Assist Panel */}
      <div className="w-72 flex-shrink-0 border-l border-border overflow-y-auto scrollbar-thin p-4" style={{ background: 'var(--ai-bg)' }}>
        <div className="flex items-center gap-2 mb-4">
          <Icon name="SparklesIcon" size={14} className="text-primary" />
          <h3 className="text-[13px] font-700 text-primary">CX AI Assist</h3>
          <span className="ai-badge">AI</span>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-green-200 bg-green-50 text-[12px] font-600 text-green-700 mb-4">
          <Icon name="CheckCircleIcon" size={13} />
          Email Analysis Complete
        </div>
        <div className="space-y-3">
          <div className="card-elevated p-3">
            <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Email Summary</div>
            <div className="text-[11px] text-foreground leading-relaxed">Citizen is requesting the current status of an STR application submitted previously. Reference: STR-2026-084521.</div>
          </div>
          <div className="card-elevated p-3">
            <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Sentiment</div>
            <SentimentBadge sentiment="Concerned" showBg confidence={88} size="md" />
          </div>
          <div className="card-elevated p-3">
            <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Detected Intent</div>
            <div className="text-[12px] font-700 text-foreground">STR Application Status</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">Agency: KPKT</div>
          </div>
          <div className="card-elevated p-3">
            <div className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground mb-2">Suggested Reply</div>
            <div className="text-[11px] text-foreground italic bg-ai-bg border border-ai-border rounded-lg p-2 mb-2">
              "Terima kasih kerana menghubungi MyGCC. Kami akan menyemak status permohonan STR anda dan memberikan kemas kini dalam masa 1-2 hari bekerja."
            </div>
            <button onClick={() => setReplyText("Terima kasih kerana menghubungi MyGCC. Kami akan menyemak status permohonan STR anda dan memberikan kemas kini dalam masa 1-2 hari bekerja.")}
              className="btn-secondary px-2.5 py-1 text-[11px] w-full">Use Draft</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Mobile Voice Workspace ───────────────────────────────────────────────────
function MobileVoiceWorkspace({
  callState, callDuration, formatDuration, showConsult, showTransfer, showWrapUp, caseCreated,
  onEndCall, onToggleTransfer, onConsult, onCloseConsult, onOpenKnowledge, onCreateCase, onDiscardWrapUp,
}: {
  callState: CallState;
  callDuration: number;
  formatDuration: (s: number) => string;
  showConsult: boolean;
  showTransfer: boolean;
  showWrapUp: boolean;
  caseCreated: boolean;
  onEndCall: () => void;
  onToggleTransfer: () => void;
  onConsult: () => void;
  onCloseConsult: () => void;
  onOpenKnowledge: () => void;
  onCreateCase: (caseId: string) => void;
  onDiscardWrapUp: () => void;
}) {
  const [mobileTab, setMobileTab] = useState<'call' | 'customer360' | 'ai'>('call');

  const mobileTabs = [
    { id: 'call' as const, label: 'Call', icon: 'PhoneIcon' },
    { id: 'customer360' as const, label: 'Customer 360', icon: 'UserCircleIcon' },
    { id: 'ai' as const, label: 'CX AI', icon: 'SparklesIcon' },
  ];

  return (
    <div className="flex-1 flex flex-col min-h-0 md:hidden">
      {/* Mobile tab switcher */}
      <div className="flex border-b border-border bg-card flex-shrink-0">
        {mobileTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setMobileTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[12px] font-600 border-b-2 transition-colors ${
              mobileTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'
            }`}
          >
            <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={13} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Mobile content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {mobileTab === 'call' && (
          <div>
            {showConsult ? (
              <ConsultPanel onClose={onCloseConsult} onComplete={() => { onCloseConsult(); }} />
            ) : showTransfer ? (
              <TransferPanel onClose={onToggleTransfer} />
            ) : (showWrapUp || callState === 'ai-draft-ready') ? (
              <AIWrapUp duration={formatDuration(callDuration)} onCreateCase={onCreateCase} onDiscard={onDiscardWrapUp} />
            ) : (
              <CallColumn
                callState={callState}
                duration={formatDuration(callDuration)}
                onEndCall={onEndCall}
                showTransfer={showTransfer}
                onToggleTransfer={onToggleTransfer}
                onConsult={onConsult}
              />
            )}
          </div>
        )}
        {mobileTab === 'customer360' && (
          <Customer360Column callState={callState} caseCreated={caseCreated} />
        )}
        {mobileTab === 'ai' && (
          <AIAssistColumn callState={callState} onOpenKnowledge={onOpenKnowledge} />
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ActiveInteractionContent() {
  const [tabs, setTabs] = useState<InteractionTab[]>(initialTabs.map(t => ({
    ...t,
    mirRef: t.channel !== 'PHONE' ? getMirRef(t.channel) : undefined,
  })));
  const [activeTab, setActiveTab] = useState('tab-phone-001');
  const [callState, setCallState] = useState<CallState>('active');
  const [callDuration, setCallDuration] = useState(156);
  const [showWrapUp, setShowWrapUp] = useState(false);
  const [caseCreated, setCaseCreated] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showConsult, setShowConsult] = useState(false);
  const [showKnowledge, setShowKnowledge] = useState(false);

  // Non-voice interaction durations
  const [interactionDurations, setInteractionDurations] = useState<Record<string, number>>({
    'tab-wa-002': 312,
    'tab-chat-003': 228,
    'tab-email-004': 480,
  });

  // Message states — deterministic sequences
  const [waMessages, setWaMessages] = useState(waConversationSequence);
  const [waDraft, setWaDraft] = useState('');
  const [waReplyIndex, setWaReplyIndex] = useState(0);

  const [chatMessages, setChatMessages] = useState(chatConversationSequence);
  const [chatDraft, setChatDraft] = useState('');
  const [chatReplyIndex, setChatReplyIndex] = useState(0);

  const [xMessages, setXMessages] = useState(xConversationSequence);
  const [xDraft, setXDraft] = useState('');
  const [xReplyIndex, setXReplyIndex] = useState(0);

  const [fbMessages, setFbMessages] = useState(fbConversationSequence);
  const [fbDraft, setFbDraft] = useState('');
  const [fbReplyIndex, setFbReplyIndex] = useState(0);

  const [igMessages, setIgMessages] = useState(igConversationSequence);
  const [igDraft, setIgDraft] = useState('');
  const [igReplyIndex, setIgReplyIndex] = useState(0);

  // Notes state per tab
  const [notesState, setNotesState] = useState<Record<string, string>>({});
  const [showNotesTab, setShowNotesTab] = useState<string | null>(null);

  // Create ticket / duplicate protection
  const [showCreateTicket, setShowCreateTicket] = useState<string | null>(null); // tabId
  const [showDuplicateWarning, setShowDuplicateWarning] = useState<string | null>(null); // tabId
  const [showViewTicket, setShowViewTicket] = useState<string | null>(null); // tabId

  // End conversation confirmation
  const [showEndConfirm, setShowEndConfirm] = useState<string | null>(null); // tabId

  // Incoming simulation
  const [incomingChannel, setIncomingChannel] = useState<IncomingChannel | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const durationTimersRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  // Voice call timer
  useEffect(() => {
    if (callState === 'active') {
      timerRef.current = setInterval(() => setCallDuration(d => d + 1), 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [callState]);

  // Non-voice interaction timers
  useEffect(() => {
    tabs.forEach(tab => {
      if (tab.channel !== 'PHONE' && tab.lifecycle === 'active' && !durationTimersRef.current[tab.id]) {
        durationTimersRef.current[tab.id] = setInterval(() => {
          setInteractionDurations(prev => ({ ...prev, [tab.id]: (prev[tab.id] || 0) + 1 }));
        }, 1000);
      }
    });
    return () => {};
  }, [tabs]);

  const formatDuration = (secs: number) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleEndCall = useCallback(() => {
    setCallState('ended');
    toast.info('Call ended — duration ' + formatDuration(callDuration));
    setTabs(prev => prev.map(t => t.id === 'tab-phone-001' ? { ...t, status: 'ended' } : t));
    setTimeout(() => {
      setCallState('ai-processing');
      setTabs(prev => prev.map(t => t.id === 'tab-phone-001' ? { ...t, status: 'ai-processing' } : t));
      toast.info('✨ AI Processing — Ahmed Firdaus', { description: 'CX AI Assist is analysing the interaction recording' });
    }, 1500);
    setTimeout(() => {
      setCallState('ai-draft-ready');
      setTabs(prev => prev.map(t => t.id === 'tab-phone-001' ? { ...t, status: 'ai-draft-ready', unread: 1 } : t));
      toast.success('✨ AI Draft Ready — Ahmed Firdaus', {
        description: 'CX AI Assist has prepared a ticket draft for Ahmed Firdaus',
        duration: 6000,
      });
    }, 8000);
  }, [callDuration]);

  const handleOpenWrapUp = useCallback(() => {
    setShowWrapUp(true);
    setTabs(prev => prev.map(t => t.id === 'tab-phone-001' ? { ...t, unread: undefined } : t));
  }, []);

  const handleCreateCase = useCallback((caseId: string) => {
    setCaseCreated(true);
    setShowWrapUp(false);
    toast.success(`Case ${caseId} created successfully`, {
      description: 'Assigned to Kementerian Kesihatan Malaysia (KKM) · Resolved at First Contact',
      duration: 5000,
    });
  }, []);

  // Non-voice: trigger end conversation flow
  const handleEndInteraction = (tabId: string) => {
    setShowEndConfirm(tabId);
  };

  const handleConfirmEnd = (tabId: string) => {
    setShowEndConfirm(null);
    // Stop duration timer
    if (durationTimersRef.current[tabId]) {
      clearInterval(durationTimersRef.current[tabId]);
      delete durationTimersRef.current[tabId];
    }
    // Set lifecycle to ai-processing
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, lifecycle: 'ai-processing', status: 'ai-processing' } : t));
    toast.info('Conversation ended — AI is processing the interaction');

    // After delay, set to ai-draft-ready
    setTimeout(() => {
      setTabs(prev => prev.map(t => t.id === tabId ? { ...t, lifecycle: 'ai-draft-ready', status: 'ai-draft-ready', unread: 1 } : t));
      const tab = tabs.find(t => t.id === tabId);
      toast.success(`✨ AI Draft Ready — ${tab?.citizenFull || tab?.citizen}`, {
        description: 'CX AI Assist has prepared the interaction wrap-up for your review',
        duration: 6000,
      });
    }, 5000);
  };

  // Create ticket
  const handleCreateTicket = (tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;
    if (tab.linkedTicket) {
      setShowDuplicateWarning(tabId);
      return;
    }
    setShowCreateTicket(tabId);
  };

  const handleTicketCreated = (tabId: string, ticket: LinkedTicket) => {
    setShowCreateTicket(null);
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, linkedTicket: ticket } : t));
    toast.success(`Ticket ${ticket.id} created`, { description: `${ticket.subject} · Open` });
  };

  const handleUnlinkTicket = (tabId: string) => {
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, linkedTicket: null } : t));
    toast.info('Ticket unlinked from interaction');
  };

  // Non-voice wrap-up complete
  const handleNonVoiceWrapUpComplete = (tabId: string, ticketId: string) => {
    setTabs(prev => prev.filter(t => t.id !== tabId));
    const nextTab = tabs.find(t => t.id !== tabId);
    if (nextTab) setActiveTab(nextTab.id);
    toast.success('Interaction completed', { description: `Ticket ${ticketId} resolved · Customer Journey Updated` });
  };

  const sendMessage = (
    draft: string,
    setMessages: React.Dispatch<React.SetStateAction<typeof waConversationSequence>>,
    setDraft: (v: string) => void,
    citizenReplies: string[],
    replyIndex: number,
    setReplyIndex: (i: number) => void,
  ) => {
    if (!draft.trim()) return;
    const now = new Date();
    const time = `${now.getHours().toString().padStart(2,'0')}:${now.getMinutes().toString().padStart(2,'0')}`;
    setMessages(prev => [...prev, { id: prev.length + 1, from: 'agent', text: draft, time, status: 'sent' }]);
    setDraft('');
    toast.success('Message sent');
    if (replyIndex < citizenReplies.length) {
      const reply = citizenReplies[replyIndex];
      setTimeout(() => {
        setMessages(prev => [...prev, { id: prev.length + 1, from: 'citizen', text: reply, time, status: 'read' }]);
        setReplyIndex(replyIndex + 1);
      }, 2500);
    }
  };

  const sendWA = () => sendMessage(waDraft, setWaMessages, setWaDraft, waCitizenReplies, waReplyIndex, setWaReplyIndex);
  const sendChat = () => sendMessage(chatDraft, setChatMessages, setChatDraft, chatCitizenReplies, chatReplyIndex, setChatReplyIndex);
  const sendX = () => sendMessage(xDraft, setXMessages as React.Dispatch<React.SetStateAction<typeof waConversationSequence>>, setXDraft, xCitizenReplies, xReplyIndex, setXReplyIndex);
  const sendFB = () => sendMessage(fbDraft, setFbMessages as React.Dispatch<React.SetStateAction<typeof waConversationSequence>>, setFbDraft, fbCitizenReplies, fbReplyIndex, setFbReplyIndex);
  const sendIG = () => sendMessage(igDraft, setIgMessages as React.Dispatch<React.SetStateAction<typeof waConversationSequence>>, setIgDraft, igCitizenReplies, igReplyIndex, setIgReplyIndex);

  const handleSimulate = (channel: SimulateChannel) => {
    const data = channelSimData[channel];
    if (channel === 'voice') {
      // Reset any existing seeded/mock voice interaction transparently
      // then show the incoming call notification
      const existingVoiceTab = tabs.find(t => t.channel === 'PHONE');
      if (existingVoiceTab) {
        setTabs(prev => prev.filter(t => t.channel !== 'PHONE'));
        setCallState('active');
        setCallDuration(0);
        setShowWrapUp(false);
      }
      setIncomingChannel('voice');
      return;
    }
    setIncomingChannel(channel as IncomingChannel);
  };

  const handleEndAndRestartVoice = () => {
    setTabs(prev => prev.filter(t => t.channel !== 'PHONE'));
    setCallState('active');
    setCallDuration(0);
    setShowWrapUp(false);
    setIncomingChannel('voice');
  };

  const handleAcceptIncoming = () => {
    if (!incomingChannel) return;
    const data = channelSimData[incomingChannel];
    const existingTab = tabs.find(t => t.channel === data.channel && t.citizenFull === data.citizenFull);
    if (!existingTab) {
      const newMirRef = data.channel !== 'PHONE' ? getMirRef(data.channel) : undefined;
      const newTab: InteractionTab = {
        id: `tab-sim-${Date.now()}`,
        channel: data.channel,
        citizen: data.citizen,
        citizenFull: data.citizenFull,
        topic: data.topic,
        sentiment: 'Neutral',
        unread: 1,
        lifecycle: 'active',
        mirRef: newMirRef,
      };
      setTabs(prev => [...prev, newTab]);
      if (data.channel !== 'PHONE') {
        setInteractionDurations(prev => ({ ...prev, [newTab.id]: 0 }));
      }
      toast.success(`${data.citizenFull} added to Active Interactions`, { description: data.topic });
    }
    setIncomingChannel(null);
  };

  const handleDeclineIncoming = () => {
    setIncomingChannel(null);
    toast.info('Interaction declined');
  };

  const handleReset = () => {
    // Clear all timers
    Object.values(durationTimersRef.current).forEach(clearInterval);
    durationTimersRef.current = {};

    const resetTabs = initialTabs.map(t => ({
      ...t,
      mirRef: t.channel !== 'PHONE' ? getMirRef(t.channel) : undefined,
      linkedTicket: null,
      lifecycle: 'active' as NonVoiceLifecycle,
    }));
    setTabs(resetTabs);
    setActiveTab('tab-phone-001');
    setCallState('active');
    setCallDuration(156);
    setShowWrapUp(false);
    setCaseCreated(false);
    setShowTransfer(false);
    setShowConsult(false);
    setWaMessages(waConversationSequence);
    setWaDraft('');
    setWaReplyIndex(0);
    setChatMessages(chatConversationSequence);
    setChatDraft('');
    setChatReplyIndex(0);
    setXMessages(xConversationSequence);
    setXDraft('');
    setXReplyIndex(0);
    setFbMessages(fbConversationSequence);
    setFbDraft('');
    setFbReplyIndex(0);
    setIgMessages(igConversationSequence);
    setIgDraft('');
    setIgReplyIndex(0);
    setInteractionDurations({ 'tab-wa-002': 312, 'tab-chat-003': 228, 'tab-email-004': 480 });
    setNotesState({});
    setShowNotesTab(null);
    setShowCreateTicket(null);
    setShowDuplicateWarning(null);
    setShowViewTicket(null);
    setShowEndConfirm(null);
    setIncomingChannel(null);
    toast.success('Demo state reset successfully', { description: 'All interactions restored to initial configuration' });
  };

  const isPhoneActive = activeTab === 'tab-phone-001';
  const isWAActive = activeTab === 'tab-wa-002';
  const isChatActive = activeTab === 'tab-chat-003';
  const isEmailActive = activeTab === 'tab-email-004';
  const currentTab = tabs.find(t => t.id === activeTab);
  const hasActiveVoice = callState === 'active' || callState === 'ai-processing';

  const isSimulatedTab = currentTab && !['tab-phone-001', 'tab-wa-002', 'tab-chat-003', 'tab-email-004'].includes(activeTab);
  const isXTab = isSimulatedTab && currentTab?.channel === 'X';
  const isFBTab = isSimulatedTab && currentTab?.channel === 'FACEBOOK';
  const isIGTab = isSimulatedTab && currentTab?.channel === 'INSTAGRAM';

  // Tab status label
  const getTabStatusLabel = (tab: InteractionTab): string => {
    if (tab.channel === 'PHONE') {
      if (callState === 'ai-processing') return 'AI Processing';
      if (callState === 'ai-draft-ready') return 'AI Draft Ready';
      return 'Active';
    }
    switch (tab.lifecycle) {
      case 'ai-processing': return 'Wrap-Up';
      case 'ai-draft-ready': return 'AI Draft Ready';
      default: return 'Active';
    }
  };

  // Current tab's linked ticket
  const currentLinkedTicket = currentTab?.linkedTicket || null;
  const currentMirRef = currentTab?.mirRef || `MIR-SCR-20260816-15021`;
  const currentDuration = currentTab ? formatDuration(interactionDurations[currentTab.id] || 0) : '00:00';
  const currentLifecycle = currentTab?.lifecycle || 'active';

  // Show wrap-up panel for non-voice
  const showNonVoiceWrapUp = currentTab && currentTab.channel !== 'PHONE' && currentTab.lifecycle === 'ai-draft-ready';

  return (
    <div className="flex flex-col h-full">
      {/* Incoming notification */}
      {incomingChannel && (
        <IncomingInteractionNotification
          data={{ channel: incomingChannel, ...channelSimData[incomingChannel] }}
          onAccept={handleAcceptIncoming}
          onDecline={handleDeclineIncoming}
        />
      )}

      {/* Create Ticket Form */}
      {showCreateTicket && (() => {
        const tab = tabs.find(t => t.id === showCreateTicket);
        if (!tab) return null;
        return (
          <CreateTicketForm
            citizen={tab.citizen}
            citizenFull={tab.citizenFull || tab.citizen}
            channel={tab.channel}
            topic={tab.topic}
            onCancel={() => setShowCreateTicket(null)}
            onCreate={(ticket) => handleTicketCreated(showCreateTicket, ticket)}
          />
        );
      })()}

      {/* Duplicate Ticket Warning */}
      {showDuplicateWarning && (() => {
        const tab = tabs.find(t => t.id === showDuplicateWarning);
        if (!tab?.linkedTicket) return null;
        return (
          <DuplicateTicketWarning
            ticket={tab.linkedTicket}
            onViewExisting={() => { setShowDuplicateWarning(null); setShowViewTicket(showDuplicateWarning); }}
            onCreateAnother={() => { setShowDuplicateWarning(null); setShowCreateTicket(showDuplicateWarning); }}
            onCancel={() => setShowDuplicateWarning(null)}
          />
        );
      })()}

      {/* End Conversation Confirm */}
      {showEndConfirm && (() => {
        const tab = tabs.find(t => t.id === showEndConfirm);
        if (!tab) return null;
        return (
          <EndConversationConfirm
            citizen={tab.citizenFull || tab.citizen}
            channel={tab.channel}
            duration={formatDuration(interactionDurations[tab.id] || 0)}
            linkedTicket={tab.linkedTicket || null}
            onContinue={() => setShowEndConfirm(null)}
            onEndAndWrapUp={() => handleConfirmEnd(showEndConfirm)}
          />
        );
      })()}

      {/* Knowledge modal */}
      {showKnowledge && (
        <KnowledgeModal
          onClose={() => setShowKnowledge(false)}
          onUse={(text) => {
            if (isChatActive) setChatDraft(text);
            else if (isWAActive) setWaDraft(text);
            toast.success('Knowledge article inserted into composer');
          }}
        />
      )}

      {/* Top bar: Interaction Tabs + Simulate Demo */}
      <div className="flex items-center gap-0 border-b border-border bg-card px-2 sm:px-4 pt-2 flex-shrink-0">
        <div className="flex items-center gap-0 flex-1 overflow-x-auto scrollbar-none" style={{ WebkitOverflowScrolling: 'touch' }}>
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const isPhone = tab.channel === 'PHONE';
            const isAIProcessing = isPhone && callState === 'ai-processing';
            const isAIDraftReady = isPhone && callState === 'ai-draft-ready';
            const isNonVoiceProcessing = !isPhone && tab.lifecycle === 'ai-processing';
            const isNonVoiceDraftReady = !isPhone && tab.lifecycle === 'ai-draft-ready';
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (isAIDraftReady && !showWrapUp) handleOpenWrapUp();
                  setTabs(prev => prev.map(t => t.id === tab.id ? { ...t, unread: undefined } : t));
                }}
                className={`relative flex items-center gap-2 px-3.5 py-2 text-[12px] font-500 border-b-2 transition-all duration-150 mr-1 rounded-t flex-shrink-0
                  ${isActive ? 'interaction-tab-active' : 'interaction-tab-inactive'}`}
              >
                <Icon name={getChannelIcon(tab.channel) as Parameters<typeof Icon>[0]['name']} size={13} className={getChannelColor(tab.channel)} />
                <span className="font-600 max-w-[80px] truncate">{tab.citizen.split(' ')[0]}</span>
                <span className="text-[12px]">{getSentimentEmoji(tab.sentiment)}</span>
                {isPhone && callState === 'active' && (
                  <span className="flex items-center gap-1 text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded-md font-700">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                    {formatDuration(callDuration)}
                  </span>
                )}
                {isAIProcessing && (
                  <span className="flex items-center gap-1 text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-md font-600">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                    AI Processing
                  </span>
                )}
                {isAIDraftReady && (
                  <span className="flex items-center gap-1 text-[10px] bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded-md font-700">
                    ✨ Draft Ready
                  </span>
                )}
                {isNonVoiceProcessing && (
                  <span className="flex items-center gap-1 text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-md font-600">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                    Wrap-Up
                  </span>
                )}
                {isNonVoiceDraftReady && (
                  <span className="flex items-center gap-1 text-[10px] bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded-md font-700">
                    ✨ AI Draft Ready
                  </span>
                )}
                {tab.unread && !isActive && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-700 rounded-full flex items-center justify-center">{tab.unread}</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex-shrink-0 ml-2 mb-1.5">
          <SimulateDemoDropdown
            onSimulate={handleSimulate}
            onReset={handleReset}
            activeVoiceIsSimulated={hasActiveVoice && callDuration > 0}
            activeVoiceCitizen="Ahmed Firdaus"
            onEndAndRestartVoice={handleEndAndRestartVoice}
          />
        </div>

        {caseCreated && (
          <div className="ml-2 flex items-center gap-1.5 text-[11px] text-green-600 font-600 bg-green-50 border border-green-200 px-2.5 py-1 rounded-lg flex-shrink-0 mb-1.5">
            <Icon name="CheckCircleIcon" size={13} />
            Case MYGCC-2026-008431 Created
          </div>
        )}
      </div>

      {/* Persistent Call Bar */}
      {!isPhoneActive && (callState === 'active' || callState === 'ai-processing') && (
        <PersistentCallBar
          duration={formatDuration(callDuration)}
          callState={callState}
          onReturn={() => setActiveTab('tab-phone-001')}
          onEndCall={handleEndCall}
        />
      )}

      {/* ── Phone workspace ── */}
      {isPhoneActive && (
        <div className="flex-1 flex min-h-0 flex-col md:flex-row">
          {/* Mobile: tab navigation for Voice / Customer 360 / AI Assist */}
          <MobileVoiceWorkspace
            callState={callState}
            callDuration={callDuration}
            formatDuration={formatDuration}
            showConsult={showConsult}
            showTransfer={showTransfer}
            showWrapUp={showWrapUp}
            caseCreated={caseCreated}
            onEndCall={handleEndCall}
            onToggleTransfer={() => { setShowTransfer(v => !v); setShowConsult(false); }}
            onConsult={() => { setShowConsult(true); setShowTransfer(false); }}
            onCloseConsult={() => setShowConsult(false)}
            onOpenKnowledge={() => setShowKnowledge(true)}
            onCreateCase={handleCreateCase}
            onDiscardWrapUp={() => setShowWrapUp(false)}
          />
          {/* Desktop: 3-column layout */}
          <div className="hidden md:flex flex-1 min-h-0">
            <div className="w-64 xl:w-72 flex-shrink-0 border-r border-border overflow-y-auto scrollbar-thin bg-card">
              {showConsult ? (
                <ConsultPanel onClose={() => setShowConsult(false)} onComplete={() => { setShowConsult(false); toast.success('Transfer completed'); }} />
              ) : (
                <CallColumn
                  callState={callState}
                  duration={formatDuration(callDuration)}
                  onEndCall={handleEndCall}
                  showTransfer={showTransfer}
                  onToggleTransfer={() => { setShowTransfer(v => !v); setShowConsult(false); }}
                  onConsult={() => { setShowConsult(true); setShowTransfer(false); }}
                />
              )}
            </div>
            <div className="flex-1 min-w-0 overflow-y-auto scrollbar-thin bg-background">
              {showTransfer ? (
                <TransferPanel onClose={() => setShowTransfer(false)} />
              ) : (
                <Customer360Column callState={callState} caseCreated={caseCreated} />
              )}
            </div>
            <div className="w-72 xl:w-80 flex-shrink-0 border-l border-border overflow-y-auto scrollbar-thin" style={{ background: 'var(--ai-bg)' }}>
              {showWrapUp || callState === 'ai-draft-ready' ? (
                <AIWrapUp duration={formatDuration(callDuration)} onCreateCase={handleCreateCase} onDiscard={() => setShowWrapUp(false)} />
              ) : (
                <AIAssistColumn callState={callState} onOpenKnowledge={() => setShowKnowledge(true)} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── WhatsApp workspace ── */}
      {isWAActive && (() => {
        const tab = tabs.find(t => t.id === 'tab-wa-002')!;
        const lifecycle = tab?.lifecycle || 'active';
        if (showViewTicket === 'tab-wa-002' && tab?.linkedTicket) {
          return (
            <TicketDetailView
              ticket={tab.linkedTicket}
              citizen={tab.citizenFull || tab.citizen}
              channel={tab.channel}
              mirRef={tab.mirRef || currentMirRef}
              onBack={() => setShowViewTicket(null)}
            />
          );
        }
        if (lifecycle === 'ai-draft-ready') {
          return (
            <div className="flex-1 flex min-h-0">
              <div className="flex-1 overflow-y-auto scrollbar-thin" style={{ background: 'var(--ai-bg)' }}>
                <NonVoiceAIWrapUp
                  channel={tab.channel}
                  citizen={tab.citizen}
                  citizenFull={tab.citizenFull || tab.citizen}
                  duration={formatDuration(interactionDurations['tab-wa-002'] || 0)}
                  linkedTicket={tab.linkedTicket || null}
                  mirRef={tab.mirRef || currentMirRef}
                  onConfirmResolve={(ticketId) => handleNonVoiceWrapUpComplete('tab-wa-002', ticketId)}
                  onKeepOpen={() => handleNonVoiceWrapUpComplete('tab-wa-002', tab.linkedTicket?.id || 'MYGCC-TKT-2026-XXXXX')}
                  onEscalate={() => toast.info('Escalation initiated')}
                  onDiscard={() => setTabs(prev => prev.map(t => t.id === 'tab-wa-002' ? { ...t, lifecycle: 'active' } : t))}
                />
              </div>
            </div>
          );
        }
        return (
          <ChatWorkspace
            tab={tab}
            messages={waMessages}
            draft={waDraft}
            setDraft={setWaDraft}
            onSend={sendWA}
            onEndInteraction={() => handleEndInteraction('tab-wa-002')}
            aiSuggestion="Saya faham situasi puan. Biar saya semak langkah yang berkaitan dan bantu puan satu per satu untuk proses renewal passport."
            onUseAI={() => setWaDraft("Saya faham situasi puan. Biar saya semak langkah yang berkaitan dan bantu puan satu per satu untuk proses renewal passport.")}
            lifecycle={lifecycle}
            linkedTicket={tab.linkedTicket || null}
            mirRef={tab.mirRef || currentMirRef}
            interactionDuration={formatDuration(interactionDurations['tab-wa-002'] || 0)}
            onCreateTicket={() => handleCreateTicket('tab-wa-002')}
            onViewTicket={() => setShowViewTicket('tab-wa-002')}
            onUnlinkTicket={() => handleUnlinkTicket('tab-wa-002')}
            onOpenKnowledge={() => setShowKnowledge(true)}
            showNotes={showNotesTab === 'tab-wa-002'}
            onToggleNotes={() => setShowNotesTab(prev => prev === 'tab-wa-002' ? null : 'tab-wa-002')}
          />
        );
      })()}

      {/* ── Live Chat workspace ── */}
      {isChatActive && (() => {
        const tab = tabs.find(t => t.id === 'tab-chat-003')!;
        const lifecycle = tab?.lifecycle || 'active';
        if (showViewTicket === 'tab-chat-003' && tab?.linkedTicket) {
          return (
            <TicketDetailView
              ticket={tab.linkedTicket}
              citizen={tab.citizenFull || tab.citizen}
              channel={tab.channel}
              mirRef={tab.mirRef || currentMirRef}
              onBack={() => setShowViewTicket(null)}
            />
          );
        }
        if (lifecycle === 'ai-draft-ready') {
          return (
            <div className="flex-1 flex min-h-0">
              <div className="flex-1 overflow-y-auto scrollbar-thin" style={{ background: 'var(--ai-bg)' }}>
                <NonVoiceAIWrapUp
                  channel={tab.channel}
                  citizen={tab.citizen}
                  citizenFull={tab.citizenFull || tab.citizen}
                  duration={formatDuration(interactionDurations['tab-chat-003'] || 0)}
                  linkedTicket={tab.linkedTicket || null}
                  mirRef={tab.mirRef || currentMirRef}
                  onConfirmResolve={(ticketId) => handleNonVoiceWrapUpComplete('tab-chat-003', ticketId)}
                  onKeepOpen={() => handleNonVoiceWrapUpComplete('tab-chat-003', tab.linkedTicket?.id || 'MYGCC-TKT-2026-XXXXX')}
                  onEscalate={() => toast.info('Escalation initiated')}
                  onDiscard={() => setTabs(prev => prev.map(t => t.id === 'tab-chat-003' ? { ...t, lifecycle: 'active' } : t))}
                />
              </div>
            </div>
          );
        }
        return (
          <ChatWorkspace
            tab={tab}
            messages={chatMessages}
            draft={chatDraft}
            setDraft={setChatDraft}
            onSend={sendChat}
            onEndInteraction={() => handleEndInteraction('tab-chat-003')}
            aiSuggestion="Salam Puan Siti. Untuk pembaharuan pasport, puan perlu menyediakan IC asal, pasport lama, borang IMM.12 dan bayaran pemprosesan. Masa pemprosesan adalah 3-5 hari bekerja."
            onUseAI={() => setChatDraft("Salam Puan Siti. Untuk pembaharuan pasport, puan perlu menyediakan IC asal, pasport lama, borang IMM.12 dan bayaran pemprosesan. Masa pemprosesan adalah 3-5 hari bekerja.")}
            lifecycle={lifecycle}
            linkedTicket={tab.linkedTicket || null}
            mirRef={tab.mirRef || currentMirRef}
            interactionDuration={formatDuration(interactionDurations['tab-chat-003'] || 0)}
            onCreateTicket={() => handleCreateTicket('tab-chat-003')}
            onViewTicket={() => setShowViewTicket('tab-chat-003')}
            onUnlinkTicket={() => handleUnlinkTicket('tab-chat-003')}
            onOpenKnowledge={() => setShowKnowledge(true)}
            showNotes={showNotesTab === 'tab-chat-003'}
            onToggleNotes={() => setShowNotesTab(prev => prev === 'tab-chat-003' ? null : 'tab-chat-003')}
          />
        );
      })()}

      {/* ── Email workspace ── */}
      {isEmailActive && (() => {
        const tab = tabs.find(t => t.id === 'tab-email-004')!;
        const lifecycle = tab?.lifecycle || 'active';
        if (showViewTicket === 'tab-email-004' && tab?.linkedTicket) {
          return (
            <TicketDetailView
              ticket={tab.linkedTicket}
              citizen={tab.citizenFull || tab.citizen}
              channel={tab.channel}
              mirRef={tab.mirRef || currentMirRef}
              onBack={() => setShowViewTicket(null)}
            />
          );
        }
        if (lifecycle === 'ai-draft-ready') {
          return (
            <div className="flex-1 flex min-h-0">
              <div className="flex-1 overflow-y-auto scrollbar-thin" style={{ background: 'var(--ai-bg)' }}>
                <NonVoiceAIWrapUp
                  channel={tab.channel}
                  citizen={tab.citizen}
                  citizenFull={tab.citizenFull || tab.citizen}
                  duration={formatDuration(interactionDurations['tab-email-004'] || 0)}
                  linkedTicket={tab.linkedTicket || null}
                  mirRef={tab.mirRef || currentMirRef}
                  onConfirmResolve={(ticketId) => handleNonVoiceWrapUpComplete('tab-email-004', ticketId)}
                  onKeepOpen={() => handleNonVoiceWrapUpComplete('tab-email-004', tab.linkedTicket?.id || 'MYGCC-TKT-2026-XXXXX')}
                  onEscalate={() => toast.info('Escalation initiated')}
                  onDiscard={() => setTabs(prev => prev.map(t => t.id === 'tab-email-004' ? { ...t, lifecycle: 'active' } : t))}
                />
              </div>
            </div>
          );
        }
        return (
          <EmailWorkspace
            tab={tab}
            lifecycle={lifecycle}
            linkedTicket={tab.linkedTicket || null}
            mirRef={tab.mirRef || currentMirRef}
            interactionDuration={formatDuration(interactionDurations['tab-email-004'] || 0)}
            onCreateTicket={() => handleCreateTicket('tab-email-004')}
            onViewTicket={() => setShowViewTicket('tab-email-004')}
            onUnlinkTicket={() => handleUnlinkTicket('tab-email-004')}
            onEndInteraction={() => handleEndInteraction('tab-email-004')}
            showNotes={showNotesTab === 'tab-email-004'}
            onToggleNotes={() => setShowNotesTab(prev => prev === 'tab-email-004' ? null : 'tab-email-004')}
          />
        );
      })()}

      {/* ── Simulated Social tabs ── */}
      {isXTab && currentTab && (
        <SocialWorkspace
          tab={currentTab}
          messages={xMessages as typeof waConversationSequence}
          draft={xDraft}
          setDraft={setXDraft}
          onSend={sendX}
          onEndInteraction={() => handleEndInteraction(currentTab.id)}
          aiSuggestion="Thank you for reaching out to MyGCC. You can check your government assistance application status by visiting portal.gov.my or calling 1-300-88-8000."
          onUseAI={() => setXDraft("Thank you for reaching out to MyGCC. You can check your government assistance application status by visiting portal.gov.my or calling 1-300-88-8000.")}
          lifecycle={currentTab.lifecycle || 'active'}
        />
      )}

      {isFBTab && currentTab && (
        <SocialWorkspace
          tab={currentTab}
          messages={fbMessages as typeof waConversationSequence}
          draft={fbDraft}
          setDraft={setFbDraft}
          onSend={sendFB}
          onEndInteraction={() => handleEndInteraction(currentTab.id)}
          aiSuggestion="Salam Puan Farah. Terima kasih kerana menghubungi MyGCC. Untuk semakan status permohonan bantuan, sila layari portal.gov.my atau hubungi 1-300-88-8000."
          onUseAI={() => setFbDraft("Salam Puan Farah. Terima kasih kerana menghubungi MyGCC. Untuk semakan status permohonan bantuan, sila layari portal.gov.my atau hubungi 1-300-88-8000.")}
          lifecycle={currentTab.lifecycle || 'active'}
        />
      )}

      {isIGTab && currentTab && (
        <SocialWorkspace
          tab={currentTab}
          messages={igMessages as typeof waConversationSequence}
          draft={igDraft}
          setDraft={setIgDraft}
          onSend={sendIG}
          onEndInteraction={() => handleEndInteraction(currentTab.id)}
          aiSuggestion="Hi! Thank you for reaching out to MyGCC. To check your application status, please visit portal.gov.my or DM us your reference number."
          onUseAI={() => setIgDraft("Hi! Thank you for reaching out to MyGCC. To check your application status, please visit portal.gov.my or DM us your reference number.")}
          lifecycle={currentTab.lifecycle || 'active'}
        />
      )}

      {/* Simulated non-voice tabs (new ones from simulate) */}
      {isSimulatedTab && currentTab && !isXTab && !isFBTab && !isIGTab && currentTab.channel !== 'PHONE' && (() => {
        const lifecycle = currentTab.lifecycle || 'active';
        if (lifecycle === 'ai-draft-ready') {
          return (
            <div className="flex-1 flex min-h-0">
              <div className="flex-1 overflow-y-auto scrollbar-thin" style={{ background: 'var(--ai-bg)' }}>
                <NonVoiceAIWrapUp
                  channel={currentTab.channel}
                  citizen={currentTab.citizen}
                  citizenFull={currentTab.citizenFull || currentTab.citizen}
                  duration={formatDuration(interactionDurations[currentTab.id] || 0)}
                  linkedTicket={currentTab.linkedTicket || null}
                  mirRef={currentTab.mirRef || currentMirRef}
                  onConfirmResolve={(ticketId) => handleNonVoiceWrapUpComplete(currentTab.id, ticketId)}
                  onKeepOpen={() => handleNonVoiceWrapUpComplete(currentTab.id, currentTab.linkedTicket?.id || 'MYGCC-TKT-2026-XXXXX')}
                  onEscalate={() => toast.info('Escalation initiated')}
                  onDiscard={() => setTabs(prev => prev.map(t => t.id === currentTab.id ? { ...t, lifecycle: 'active' } : t))}
                />
              </div>
            </div>
          );
        }
        return (
          <ChatWorkspace
            tab={currentTab}
            messages={[{ id: 1, from: 'citizen', text: channelSimData[currentTab.channel.toLowerCase() as SimulateChannel]?.preview || 'Hello, I need assistance.', time: '14:50', status: 'read' }]}
            draft=""
            setDraft={() => {}}
            onSend={() => toast.success('Message sent')}
            onEndInteraction={() => handleEndInteraction(currentTab.id)}
            aiSuggestion="Thank you for contacting MyGCC. How can I assist you today?"
            onUseAI={() => {}}
            lifecycle={lifecycle}
            linkedTicket={currentTab.linkedTicket || null}
            mirRef={currentTab.mirRef || currentMirRef}
            interactionDuration={formatDuration(interactionDurations[currentTab.id] || 0)}
            onCreateTicket={() => handleCreateTicket(currentTab.id)}
            onViewTicket={() => setShowViewTicket(currentTab.id)}
            onUnlinkTicket={() => handleUnlinkTicket(currentTab.id)}
            onOpenKnowledge={() => setShowKnowledge(true)}
            showNotes={showNotesTab === currentTab.id}
            onToggleNotes={() => setShowNotesTab(prev => prev === currentTab.id ? null : currentTab.id)}
          />
        );
      })()}
    </div>
  );
}
